import { Search, Handshake, Truck, Heart } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const ProcessSection = () => {
  const steps = [
    {
      step: '01',
      title: 'Artisan Discovery',
      description: 'We travel across Bengal to discover skilled artisans preserving traditional crafts.',
      icon: <Search className="w-8 h-8" />,
      color: 'clay',
    },
    {
      step: '02',
      title: 'Collaboration',
      description: 'We work with artisans to understand their craft and ensure quality standards.',
      icon: <Handshake className="w-8 h-8" />,
      color: 'saffron',
    },
    {
      step: '03',
      title: 'Quality Check',
      description: 'Every product undergoes multiple quality checks before being listed.',
      icon: <Heart className="w-8 h-8" />,
      color: 'rose',
    },
    {
      step: '04',
      title: 'Delivery',
      description: 'Products are carefully packaged and delivered to your doorstep.',
      icon: <Truck className="w-8 h-8" />,
      color: 'green',
    },
  ];

  const getColorClasses = (color) => {
    switch (color) {
      case 'clay': return 'bg-clay-100 text-clay-600';
      case 'saffron': return 'bg-saffron-100 text-saffron-700';
      case 'rose': return 'bg-rose-100 text-rose-600';
      case 'green': return 'bg-green-100 text-green-700';
      default: return 'bg-clay-100 text-clay-600';
    }
  };

  return (
    <section className="py-16 md:py-20 bg-cream-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeInUp}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-cocoa-900">Our Process</h2>
          <p className="text-cocoa-600 max-w-2xl mx-auto text-lg">
            From artisan discovery to your doorstep - ensuring quality every step of the way
          </p>
        </motion.div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-clay-500 via-saffron-500 to-rose-500"></div>

          <div className="space-y-12 lg:space-y-0">
            {steps.map((step, index) => (
              <div
                key={step.step}
                className={`flex flex-col lg:flex-row items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
              >
                {/* Step Content */}
                <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:pr-12 text-right' : 'lg:pl-12'}`}>
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white p-8 rounded-2xl shadow-sm border border-cream-200 hover:shadow-warm transition-shadow max-w-md mx-auto lg:mx-0"
                  >
                    <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${getColorClasses(step.color)} mb-4`}>
                      {step.icon}
                    </div>
                    <div className="text-2xl font-bold text-cream-300 mb-2">{step.step}</div>
                    <h3 className="text-xl font-bold text-cocoa-900 mb-3">{step.title}</h3>
                    <p className="text-cocoa-600">{step.description}</p>
                  </motion.div>
                </div>

                {/* Timeline Dot */}
                <div className="flex items-center justify-center my-6 lg:my-0 lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2">
                  <div className={`w-8 h-8 rounded-full border-4 border-white ${getColorClasses(step.color).replace('100', '500').replace('text', 'bg')} shadow-lg`}></div>
                </div>

                {/* Empty spacer for alignment */}
                <div className="lg:w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
